import React, { createElement } from "react";
import { Navigate } from "react-router-dom";
import { Page } from "@app/shared";
import { AccountDetails } from "./account/Account";
import { CreateAccount } from "./account/CreateAccount";
import { ForgotPassword } from "./account/ForgotPassword";
import { Login } from "./account/Login";
import { ResetPassword } from "./account/ResetPassword";
import { Terms } from "./account/Terms";
import { ViewInvite } from "./account/ViewInvite";
import { Alerts } from "./Alerts";
import { ClipViewer } from "./ClipViewer";
import { ClientEndpoints } from "./ClientEndpoints";
import { EndpointApiDocsPage } from "./EndpointApiDocsPage";
import { ENDPOINT_API_DOCS_PATH } from "@app/shared/src/components/Integrations/EndpointApiDocs/paths";
import { Devices } from "./Devices";
import { NotificationSettings } from "./NotificationSettings";
import { DeviceDemo } from "./facility/DeviceDemo";
import { DeviceDetails } from "./facility/DeviceDetails";
import { FacilityDetails } from "./facility/FacilityDetails";
import { ManageUsers } from "./ManageUsers";
import { AddOrganization } from "./organization/AddOrganization";
import { DeviceLabels } from "./settings/DeviceLabels";
import { SalesLayoutPlannerPage } from "./settings/SalesLayoutPlannerPage";
import { Settings } from "./settings/Settings";
import { ViewFacilityGroup } from "./ViewFacilityGroup";
import { ViewFacilityGroups } from "./ViewFacilityGroups";
import { FacilitiesMapView } from "./FacilitiesMapView";
import { DataVisualizer } from "./visualizer/DataVisualizer";
import { ChangePassword } from "./account/ChangePassword";
import { EditProfile } from "./account/EditProfile";
import { AccountSetup } from "./wizard/AccountSetup";
import { DeviceSetup } from "./wizard/DeviceSetup";
import { OrganizationEvents } from "./organization/OrganizationEvents";
import { ScreenlineMapReportPage } from "../reports/EventReports/ScreenLineMapPage";

const basePages = {
    AccountDetails: new Page(AccountDetails, "/account"),
    Alerts: new Page(Alerts, "/alerts"),
    NotificationSettings: new Page(NotificationSettings, "/notifications"),
    ChangePassword: new Page(ChangePassword, "/account/change-password"),
    EditProfile: new Page(EditProfile, "/account/edit-profile"),
    AddOrganization: new Page(AddOrganization, "/account/add-organization"),
    ClientEndpoints: new Page(ClientEndpoints, "/client-endpoints"),
    ClipViewer: new Page(ClipViewer, "/clip"),
    EndpointApiDocs: new Page(EndpointApiDocsPage, ENDPOINT_API_DOCS_PATH),
    CreateAccount: new Page(CreateAccount, "/account/create"),
    DeviceDemo: new Page<{ id: string }>(DeviceDemo, "/device-demo", { id: true }),
    DeviceDetails: new Page<{ id: string }>(DeviceDetails, "/device-details", { id: true }),
    DeviceLabels: new Page(DeviceLabels, "/device-labels"),
    Devices: new Page(Devices, "/devices"),
    EventReports: new Page(ScreenlineMapReportPage, "/event-reports"),
    FacilityDetails: new Page<{ id: string }>(FacilityDetails, "/facilities", { id: true }),
    ForgotPassword: new Page(ForgotPassword, "/account/forgot-password"),
    Login: new Page(Login, "/login"),
    // Firmware is a Site Admin tab; on its own this route rendered the tables with no shell
    // (no header, no nav). Send it there. The tab is addressed by title, so a reorder cannot
    // break the link.
    ManageFirmware: new Page(() => createElement(Navigate, { to: "/settings?tab=Firmware", replace: true }), "/firmware"),
    OrganizationEvents: new Page(OrganizationEvents, "/organization/events"),
    ResetPassword: new Page<{ id: string }>(ResetPassword, "/account/reset-password", { id: true }),
    SalesLayoutPlanner: new Page(SalesLayoutPlannerPage, "/sales-layout-planner"),
    Settings: new Page(Settings, "/settings"),
    Users: new Page(ManageUsers, "/users"),
    ViewFacilityGroup: new Page<{ id: string }>(ViewFacilityGroup, "/facilities/group", { id: true }),
    ViewFacilityGroups: new Page(ViewFacilityGroups, "/facilities"),
    FacilitiesMapView: new Page<{ id: string }>(FacilitiesMapView, "/facilities/group/map", { id: true }),
    ViewInvite: new Page<{ id: string }>(ViewInvite, "/account/view-invite", { id: true }),
    Visualizer: new Page(DataVisualizer, "/visualizer"),
    WizardSetupAccount: new Page(AccountSetup, "/wizard/account"),
    WizardSetupDevice: new Page(DeviceSetup, "/wizard/device")
};

const createTermsWrapper = (nextPageGetter: () => string, previousPageGetter: () => string) => {
    return function TermsWrapper() {
        return React.createElement(Terms, {
            showActions: true,
            nextPage: nextPageGetter(),
            previousPage: previousPageGetter(),
        });
    };
};

const AccountTermsWrapper = createTermsWrapper(
    () => basePages.ViewFacilityGroups.getUrl(),
    () => basePages.Login.getUrl()
);

const WizardTermsWrapper = createTermsWrapper(
    () => basePages.WizardSetupDevice.getUrl() + "?newAccount=true",
    () => basePages.WizardSetupAccount.getUrl()
);

export const Pages = {
    ...basePages,
    Terms: new Page(AccountTermsWrapper, "/account/terms"),
    WizardSetupTerms: new Page(WizardTermsWrapper, "/wizard/terms"),
    DefaultRoute:basePages.ViewFacilityGroups
};
